import { isRetiredLandingHref } from '../landingDocumentUrl.js';
import type { LandingEditorProject } from './landingEditorTypes.js';

/** Remove retired branch links; preserve editorial labels, order and frame/anchor associations. */
export const migrateRetiredLandingLinks = (project: LandingEditorProject) => {
  const orders = new Map<string, { navigation: Map<number, number>; footer: Map<number, number>; industry?: Map<number, number> }>();
  let changed = false;
  for (const locale of ['de', 'en', 'tr'] as const) {
    const content = project.content[locale];
    const prune = <T extends { href: string }>(items: T[]) => {
      const order = new Map<number, number>();
      const kept = items.filter((item, index) => {
        if (isRetiredLandingHref(item.href)) { changed = true; return false; }
        order.set(index, order.size);
        return true;
      });
      return { kept, order };
    };
    const navigation = prune(content.nav);
    const footer = prune(content.footer.links);
    const industry = content.industryNav ? prune(content.industryNav) : undefined;
    content.nav = navigation.kept;
    content.footer.links = footer.kept;
    if (industry) content.industryNav = industry.kept;
    orders.set(locale, { navigation: navigation.order, footer: footer.order, industry: industry?.order });
  }
  if (!changed) return;
  const remap = <T>(entries: Record<string, T>): Record<string, T> => Object.fromEntries(
    Object.entries(entries).flatMap(([key, value]) => {
      const match = key.match(/(^|[.:])(navigation\.item|footer\.link|navigation\.industry|navigation\.mobile\.industry)\.(\d+)(?=\.|$)/u);
      if (!match) return [[key, value]];
      const locale = key.match(/(?:^|:)(de|en|tr)(?=:)/u)?.[1] ?? 'de';
      const order = orders.get(locale)!;
      const indexMap = match[2] === 'navigation.item' ? order.navigation : match[2] === 'footer.link' ? order.footer : order.industry;
      if (!indexMap) return [[key, value]];
      const next = indexMap.get(Number(match[3]));
      if (next === undefined) return [];
      return [[key.replace(match[0], `${match[1]}${match[2]}.${next}`), value]];
    }),
  );
  project.elementFrames = remap(project.elementFrames);
  project.editorAnchorIds = remap(project.editorAnchorIds);
};
